// PlanLineup — the plan fund research surface, built entirely on the reusable
// List stack (Frontend Rules §8): TableToolbar (search) + Select (asset-class
// filter) + DataTable (sort via sortRows, mobile card-collapse, memoized rows)
// + PerfNum cells + Pagination + EmptyState + a brief filter-loading shimmer.
// Held funds are highlighted via DataTable's rowClassName + a "Holding" tag.
import { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { DataTable, sortRows, Pagination, PerfNum, EmptyState, Eyebrow } from '../../reusable';
import { SearchIcon } from '../../icons/Icons';
import { useFilterLoading } from '../../../hooks';

export function PlanLineup({ funds, heldTickers, asOfLabel }) {
  const { t } = useTranslation();
  const held = useMemo(() => new Set(heldTickers), [heldTickers]);
  const assetClasses = useMemo(() => ['All', ...new Set(funds.map((f) => f.cls))], [funds]);
  const [query, setQuery] = useState('');
  const [cls, setCls] = useState('All');
  const [sort, setSort] = useState({ key: 'cls', dir: 'asc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Brief shimmer when the search/filter changes (rules: loading state always).
  const loading = useFilterLoading([query, cls], 280);

  // Reset to page 1 whenever the result set changes.
  useEffect(() => {
    setPage(1);
  }, [query, cls, pageSize]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = funds.filter((f) => {
      if (cls !== 'All' && f.cls !== cls) return false;
      if (q && !(f.ticker.toLowerCase().includes(q) || f.name.toLowerCase().includes(q))) return false;
      return true;
    });
    return sortRows(rows, sort);
  }, [funds, query, cls, sort]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);
  const heldCount = funds.filter((f) => held.has(f.ticker)).length;

  const columns = [
    {
      key: 'ticker',
      label: t('manage.lineup.colTicker'),
      sortable: true,
      render: (v) => (
        <span className="ma-tkr">
          <span className="ma-tkr__sym">{v}</span>
          {held.has(v) && <span className="ma-held-tag">{t('manage.lineup.holding')}</span>}
        </span>
      ),
    },
    { key: 'name', label: t('manage.lineup.colFund'), sortable: true, render: (v) => <span className="ma-fname">{v}</span> },
    { key: 'cls', label: t('manage.lineup.colAssetClass'), sortable: true, render: (v) => <span className="ma-muted">{v}</span> },
    { key: 'expense', label: t('manage.lineup.colExpense'), numeric: true, align: 'right', sortable: true, render: (v) => `${v.toFixed(2)}%` },
    { key: 'ytd', label: t('manage.lineup.colYtd'), numeric: true, align: 'right', sortable: true, render: (v) => <PerfNum value={v} digits={1} /> },
    { key: 'oneYr', label: t('manage.lineup.col1yr'), numeric: true, align: 'right', sortable: true, render: (v) => <PerfNum value={v} digits={1} /> },
  ];

  return (
    <div className="ma-lineup">
      {/* Header: tight eyebrow/title/sub block (left) + control pills (right). */}
      <header className="ma-lineup-h">
        <div className="ma-lineup-h__title">
          <Eyebrow>{t('manage.lineup.research')}</Eyebrow>
          <h2 className="ma-lineup-h__heading">{t('manage.lineup.title')}</h2>
          <p className="ma-lineup__sub">
            {t('manage.lineup.sub', { count: funds.length, held: heldCount })}
          </p>
        </div>
        <div className="ma-lineup__controls">
          <div className="ma-search">
            <SearchIcon width={16} height={16} />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('manage.lineup.searchPlaceholder')}
              aria-label={t('manage.lineup.searchAria')}
            />
          </div>
          <select
            className="ma-select"
            value={cls}
            onChange={(e) => setCls(e.target.value)}
            aria-label={t('manage.lineup.filterAria')}
          >
            {assetClasses.map((c) => (
              <option key={c} value={c}>
                {c === 'All' ? t('manage.lineup.all') : c}
              </option>
            ))}
          </select>
        </div>
      </header>

      <DataTable
        columns={columns}
        rows={pageRows}
        sort={sort}
        onSort={setSort}
        rowKey={(r) => r.ticker}
        rowClassName={(r) => (held.has(r.ticker) ? 'ma-row-held' : '')}
        loading={loading}
        empty={
          <EmptyState
            title={t('manage.lineup.noFundsTitle')}
            message={t('manage.lineup.noFundsMessage')}
          />
        }
      />

      {filtered.length > pageSize && (
        <Pagination
          page={page}
          pageCount={pageCount}
          onPage={setPage}
          pageSize={pageSize}
          onPageSize={setPageSize}
          pageSizeOptions={[10, 20, 50]}
        />
      )}

      {asOfLabel && (
        <div className="ma-lineup__foot">
          {t('manage.lineup.foot', { date: asOfLabel })}
        </div>
      )}
    </div>
  );
}

PlanLineup.propTypes = {
  funds: PropTypes.array.isRequired,
  heldTickers: PropTypes.arrayOf(PropTypes.string).isRequired,
  asOfLabel: PropTypes.string,
};

export default PlanLineup;
